import { useEffect, useMemo, useRef, useState } from 'react';
import type { RefObject } from 'react';
import type {
  ApproveQuoteResult,
  PublicQuoteView,
  QuoteApprovalPlacement,
  QuoteLine,
} from '@catalyst-psa/shared';
import {
  approvalAnchorForLineGroup,
  approvalAnchorForSection,
  QUOTE_LINES_FREQUENCY_LABELS,
} from '@catalyst-psa/shared';

import { PublicApiError, publicApi, saveBlob } from '../project-portal/publicApi.js';

const currency = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

function lineAmount(line: QuoteLine): number {
  const qty = Number(line.quantity);
  const price = Number(line.unitPrice);
  if (!Number.isFinite(qty) || !Number.isFinite(price)) return 0;
  return qty * price;
}

/** Bundle header lines keyed by their bundle key (header-owned bundles). */
function bundleHeaders(lines: QuoteLine[]): Map<string, QuoteLine> {
  const map = new Map<string, QuoteLine>();
  for (const line of lines) {
    if (line.isBundleHeader && line.bundleKey) map.set(line.bundleKey, line);
  }
  return map;
}

/** A line the customer can add/remove (standalone optional or part of a bundle). */
function isSelectable(line: QuoteLine, headers: Map<string, QuoteLine>): boolean {
  if (line.isBundleHeader) return Boolean(line.bundleKey);
  // Member lines of a header-owned bundle follow their header's checkbox.
  if (line.bundleKey && headers.has(line.bundleKey)) return false;
  return line.isOptional || Boolean(line.bundleKey);
}

/** Minimum picks required per bundle key (max of the group's per-line minimums). */
function bundleMinimums(lines: QuoteLine[]): Map<string, number> {
  const mins = new Map<string, number>();
  for (const line of lines) {
    if (!line.bundleKey) continue;
    mins.set(line.bundleKey, Math.max(mins.get(line.bundleKey) ?? 1, line.bundleMinChoose ?? 1));
  }
  return mins;
}

/** Maximum picks allowed per bundle key (most restrictive of the per-line maximums). */
function bundleMaximums(lines: QuoteLine[]): Map<string, number> {
  const maxes = new Map<string, number>();
  for (const line of lines) {
    if (!line.bundleKey || line.bundleMaxChoose == null) continue;
    const current = maxes.get(line.bundleKey);
    maxes.set(
      line.bundleKey,
      current === undefined ? line.bundleMaxChoose : Math.min(current, line.bundleMaxChoose),
    );
  }
  return maxes;
}

/** "Choose 1" | "Choose 1–2" | "Choose 2+" — the bundle chip's pick-count hint. */
function bundleChoiceLabel(min: number, max: number | undefined): string {
  if (max === undefined) return `Choose ${min}+`;
  if (max === min) return `Choose ${min}`;
  return `Choose ${min}–${max}`;
}

/**
 * Page-break flags as inline CSS. `break-before/after: page` is inert on screen and only
 * takes effect when the delivery page is printed, matching the PDF's layout.
 */
function pageBreakStyle(
  before?: boolean,
  after?: boolean,
): { breakBefore?: 'page'; breakAfter?: 'page' } | undefined {
  if (!before && !after) return undefined;
  return {
    ...(before ? { breakBefore: 'page' as const } : {}),
    ...(after ? { breakAfter: 'page' as const } : {}),
  };
}

function LinesTable({
  lines,
  frequencyLabel,
  mins,
  maxes,
  headers,
  included,
  onToggle,
  approved,
  hidePricing = false,
  qtyLabel,
  unitPriceLabel,
  amountLabel,
  totalLabel,
  qtyHidden,
  unitPriceHidden,
  amountHidden,
  totalHidden,
  pageBreakBefore,
  pageBreakAfter,
}: {
  /** The lines of ONE group (table). */
  lines: QuoteLine[];
  /** "One-time" | "Monthly" | "Yearly" — the group's billing frequency. */
  frequencyLabel: string;
  /** Bundle minimums computed across the WHOLE quote. */
  mins: Map<string, number>;
  /** Bundle maximums computed across the WHOLE quote. */
  maxes: Map<string, number>;
  /** Bundle header lines keyed by bundle key, computed across the WHOLE quote. */
  headers: Map<string, QuoteLine>;
  /** Whether each line is currently part of the order. */
  included: (line: QuoteLine) => boolean;
  /** Present pre-approval for selectable lines. */
  onToggle?: (line: QuoteLine) => void;
  approved: boolean;
  /** "SOW for delivery team" view: no unit price, amount, or totals. */
  hidePricing?: boolean;
  /** Column/total labels (custom or default), resolved by the caller. */
  qtyLabel: string;
  unitPriceLabel: string;
  amountLabel: string;
  totalLabel: string;
  /** Per-column hide toggles from the quote's lines-section settings. */
  qtyHidden: boolean;
  unitPriceHidden: boolean;
  amountHidden: boolean;
  totalHidden: boolean;
  /** Page breaks only take effect when the page is printed (and in the PDF). */
  pageBreakBefore?: boolean;
  pageBreakAfter?: boolean;
}): JSX.Element | null {
  if (lines.length === 0) return null;

  // Bundle totals sum the member lines whether they are shown or hidden.
  const bundleTotal = (key: string): number =>
    lines
      .filter((l) => !l.isBundleHeader && !l.isComment && l.bundleKey === key)
      .reduce((sum, l) => sum + lineAmount(l), 0);
  // Member lines of a "hide bundle lines" bundle never render — the header speaks for them.
  const visibleLines = lines.filter(
    (l) =>
      l.isBundleHeader ||
      !l.bundleKey ||
      headers.get(l.bundleKey)?.bundleLinesHidden !== true,
  );

  const rows = lines.filter((l) => !l.isComment && !l.isBundleHeader && included(l));
  const total = rows.reduce((sum, l) => sum + lineAmount(l), 0);
  const showQty = !qtyHidden;
  const showUnitPrice = !hidePricing && !unitPriceHidden;
  const showAmount = !hidePricing && !amountHidden;
  const showTotal = !hidePricing && !totalHidden;
  const columnCount =
    (onToggle ? 1 : 0) + 1 + (showQty ? 1 : 0) + (showUnitPrice ? 1 : 0) + (showAmount ? 1 : 0);

  return (
    <div
      className="my-6 overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm"
      style={pageBreakStyle(pageBreakBefore, pageBreakAfter)}
    >
      <table className="w-full text-left text-sm">
        <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
          <tr>
            {onToggle ? <th className="w-10 px-3 py-2" aria-label="Include" /> : null}
            <th className="px-4 py-2 font-medium">{frequencyLabel}</th>
            {showQty ? <th className="px-4 py-2 text-right font-medium">{qtyLabel}</th> : null}
            {showUnitPrice ? (
              <th className="px-4 py-2 text-right font-medium">{unitPriceLabel}</th>
            ) : null}
            {showAmount ? (
              <th className="px-4 py-2 text-right font-medium">{amountLabel}</th>
            ) : null}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {visibleLines.map((line) => {
            if (line.isComment) {
              return (
                <tr key={line.id}>
                  <td colSpan={columnCount} className="px-4 py-2 text-slate-600">
                    {line.description}
                  </td>
                </tr>
              );
            }
            const isIn = included(line);
            if (line.isBundleHeader) {
              return (
                <tr key={line.id} className={isIn ? 'bg-brand-50/40' : ''}>
                  {onToggle ? (
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        checked={isIn}
                        onChange={() => onToggle(line)}
                        aria-label={`Choose bundle ${line.description}`}
                        className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                      />
                    </td>
                  ) : null}
                  <td className="px-4 py-2 font-semibold text-slate-800">
                    {line.description}
                    <span className="ml-2 rounded-full bg-blue-50 px-2 py-0.5 text-xs font-normal text-blue-600">
                      Bundle · Choose 1
                    </span>
                    {approved && !isIn ? (
                      <span className="ml-2 text-xs font-normal text-slate-400">Not included</span>
                    ) : null}
                  </td>
                  {showQty ? <td className="px-4 py-2" /> : null}
                  {showUnitPrice ? <td className="px-4 py-2" /> : null}
                  {showAmount ? (
                    <td className="px-4 py-2 text-right font-semibold text-slate-800">
                      {line.bundleKey ? currency.format(bundleTotal(line.bundleKey)) : null}
                    </td>
                  ) : null}
                </tr>
              );
            }
            const selectable = isSelectable(line, headers);
            const headerOwned = Boolean(line.bundleKey && headers.has(line.bundleKey));
            return (
              <tr key={line.id} className={isIn ? '' : 'opacity-50'}>
                {onToggle ? (
                  <td className="px-3 py-2">
                    {selectable ? (
                      <input
                        type="checkbox"
                        checked={isIn}
                        onChange={() => onToggle(line)}
                        aria-label={`Include ${line.description}`}
                        className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                      />
                    ) : headerOwned ? null : (
                      <span className="text-xs text-slate-400" title="Included">✓</span>
                    )}
                  </td>
                ) : null}
                <td className="px-4 py-2 text-slate-800">
                  {line.description}
                  {line.bundleKey && !headerOwned ? (
                    <span className="ml-2 rounded-full bg-blue-50 px-2 py-0.5 text-xs text-blue-600">
                      {bundleChoiceLabel(mins.get(line.bundleKey) ?? 1, maxes.get(line.bundleKey))}
                      {' · '}
                      {line.bundleKey}
                    </span>
                  ) : line.isOptional && !headerOwned ? (
                    <span className="ml-2 rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-500">
                      Optional
                    </span>
                  ) : null}
                  {approved && !isIn ? (
                    <span className="ml-2 text-xs text-slate-400">Not included</span>
                  ) : null}
                </td>
                {showQty ? (
                  <td className="px-4 py-2 text-right text-slate-600">{Number(line.quantity)}</td>
                ) : null}
                {showUnitPrice ? (
                  <td className="px-4 py-2 text-right text-slate-600">
                    {currency.format(Number(line.unitPrice))}
                  </td>
                ) : null}
                {showAmount ? (
                  <td className="px-4 py-2 text-right text-slate-800">
                    {currency.format(lineAmount(line))}
                  </td>
                ) : null}
              </tr>
            );
          })}
          {rows.length > 0 && showTotal ? (
            <tr className="bg-slate-50 font-semibold text-slate-800">
              <td colSpan={Math.max(1, columnCount - 1)} className="px-4 py-2 text-right">
                {totalLabel}
              </td>
              <td className="px-4 py-2 text-right">{currency.format(total)}</td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </div>
  );
}

function ApprovalForm({
  token,
  previewMode,
  lines,
  selected,
  onApproved,
}: {
  /** Delivery token — absent in internal previews (approval disabled). */
  token?: string;
  previewMode: boolean;
  lines: QuoteLine[];
  selected: Set<string>;
  onApproved: () => void;
}): JSX.Element {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Every bundle needs at least its minimum (and at most its maximum) picks before
  // approval. The toggle already enforces maximums, so max problems are a safety net.
  // Header-owned bundles are radio groups: exactly ONE bundle must be chosen.
  const bundleProblems = useMemo(() => {
    const problems: string[] = [];
    const headers = bundleHeaders(lines);
    if (headers.size > 0) {
      const picked = [...headers.values()].filter((h) => selected.has(h.id)).length;
      if (picked !== 1) problems.push('Choose one of the bundle options to approve.');
    }
    const maxes = bundleMaximums(lines);
    for (const [key, min] of bundleMinimums(lines)) {
      if (headers.has(key)) continue;
      const picked = lines.filter((l) => l.bundleKey === key && selected.has(l.id)).length;
      if (picked < min) {
        problems.push(`Choose at least ${min} option${min === 1 ? '' : 's'} from ${key}.`);
      }
      const max = maxes.get(key);
      if (max !== undefined && picked > max) {
        problems.push(`Choose at most ${max} option${max === 1 ? '' : 's'} from ${key}.`);
      }
    }
    return problems;
  }, [lines, selected]);

  const canApprove =
    !previewMode &&
    Boolean(token) &&
    name.trim().length > 0 &&
    email.trim().length > 3 &&
    agreed &&
    bundleProblems.length === 0;

  const handleApprove = async (): Promise<void> => {
    if (!token) return;
    setSubmitting(true);
    setError(null);
    try {
      await publicApi.post<ApproveQuoteResult>(`/public/quotes/${token}/approve`, {
        signerName: name.trim(),
        signerEmail: email.trim(),
        agreed,
        selectedLineIds: [...selected],
      });
      onApproved();
    } catch (err) {
      setError(err instanceof PublicApiError ? err.message : 'Approval failed — please retry.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      id={APPROVAL_ANCHOR_ID}
      className="mt-8 scroll-mt-6 rounded-xl border border-slate-200 bg-slate-50 p-6"
    >
      <h2 className="mb-1 text-base font-semibold text-slate-800">Approve this quote</h2>
      <p className="mb-4 text-sm text-slate-500">
        Type your name and email below as your electronic signature. Your approval is recorded
        with a timestamp and IP address.
      </p>

      {previewMode ? (
        <p className="mb-4 rounded-md bg-blue-50 px-3 py-2 text-sm text-blue-700">
          Preview — this is what the customer sees; approving is disabled here.
        </p>
      ) : null}

      {bundleProblems.length > 0 ? (
        <div className="mb-4 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-700">
          {bundleProblems.map((p) => (
            <p key={p}>{p}</p>
          ))}
        </div>
      ) : null}

      <div className="grid gap-4 sm:grid-cols-2">
        <label className="block text-sm">
          <span className="mb-1 block font-medium text-slate-700">Full name</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={256}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          />
        </label>
        <label className="block text-sm">
          <span className="mb-1 block font-medium text-slate-700">Email</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            maxLength={320}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          />
        </label>
      </div>

      <label className="mt-4 flex items-start gap-2 text-sm text-slate-700">
        <input
          type="checkbox"
          checked={agreed}
          onChange={(e) => setAgreed(e.target.checked)}
          className="mt-0.5 h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
        />
        <span>
          I agree to the terms of this quote and authorize Catalyst to proceed with the selected
          products and services.
        </span>
      </label>

      {error ? (
        <p className="mt-3 rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</p>
      ) : null}

      <button
        type="button"
        disabled={!canApprove || submitting}
        onClick={() => void handleApprove()}
        className="mt-4 rounded-lg bg-brand-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-40"
      >
        {submitting ? 'Approving…' : 'Approve quote'}
      </button>
    </div>
  );
}

/** DOM id of a section block, used as the table-of-contents scroll target. */
function sectionAnchorId(index: number): string {
  return `quote-section-${index}`;
}

/** DOM id of a line-group table (lines 1 / lines 2), used as a TOC scroll target. */
function linesAnchorId(key: string): string {
  return `quote-lines-${key}`;
}

/** DOM id of the approval block — the table of contents links to it like a section. */
const APPROVAL_ANCHOR_ID = 'quote-approve';

/** Honours the OS "reduce motion" setting — animated scrolling is opt-out for some users. */
function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

/** A section that has a label, and so earns an entry in the table of contents. */
interface TocEntry {
  id: string;
  label: string;
  /** The approval entry is emphasised — it's the action, not just another section. */
  emphasis?: boolean;
}

/**
 * "Download PDF" — an optional keepsake copy of the quote, never a step toward approval.
 * Only the public page can serve it (the token is the credential), so internal previews
 * render it disabled rather than hiding it, to show what the customer will see.
 */
function DownloadPdfButton({
  token,
  previewMode,
}: {
  token?: string;
  previewMode: boolean;
}): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  const handleDownload = async (): Promise<void> => {
    if (!token) return;
    setBusy(true);
    setFailed(false);
    try {
      const { blob, fileName } = await publicApi.download(`/public/quotes/${token}/pdf`);
      saveBlob(blob, fileName ?? 'quote.pdf');
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col items-end gap-1">
      <button
        type="button"
        disabled={previewMode || !token || busy}
        onClick={() => void handleDownload()}
        title={
          previewMode
            ? 'Preview — the download works on the customer’s link'
            : 'Download a PDF copy of this quote'
        }
        className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 shadow-sm hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <svg
          aria-hidden="true"
          viewBox="0 0 20 20"
          fill="currentColor"
          className="h-4 w-4 text-slate-400"
        >
          <path d="M10 2.5a.75.75 0 0 1 .75.75v7.19l2.22-2.22a.75.75 0 1 1 1.06 1.06l-3.5 3.5a.75.75 0 0 1-1.06 0l-3.5-3.5a.75.75 0 0 1 1.06-1.06l2.22 2.22V3.25A.75.75 0 0 1 10 2.5Z" />
          <path d="M3.5 12.75a.75.75 0 0 1 .75.75v1.5c0 .14.11.25.25.25h11a.25.25 0 0 0 .25-.25v-1.5a.75.75 0 0 1 1.5 0v1.5A1.75 1.75 0 0 1 15.5 16.75h-11A1.75 1.75 0 0 1 2.75 15v-1.5a.75.75 0 0 1 .75-.75Z" />
        </svg>
        {busy ? 'Preparing…' : 'Download PDF'}
      </button>
      {failed ? (
        <span className="text-xs text-rose-600">Download failed — please try again.</span>
      ) : null}
    </div>
  );
}

/**
 * A table of contents pinned just off the right edge of the document, near the top. It
 * borrows the document's own card styling so it reads as part of the page rather than an
 * overlay, highlights whichever section is currently in view, and scrolls smoothly rather
 * than jumping. Hidden below xl, where there is no room beside the page.
 */
function QuoteTableOfContents({
  entries,
  scopeRef,
}: {
  entries: TocEntry[];
  /**
   * Anchor lookups are scoped to this element. The quote editor pages carry section
   * anchors with the same ids, so an unscoped getElementById inside the delivery
   * preview modal would target (and scroll) the editor behind the modal instead.
   */
  scopeRef: RefObject<HTMLElement | null>;
}): JSX.Element | null {
  const [activeId, setActiveId] = useState<string | null>(entries[0]?.id ?? null);

  useEffect(() => {
    if (entries.length === 0) return undefined;
    const scope = scopeRef.current;
    if (!scope) return undefined;
    const observer = new IntersectionObserver(
      (observed) => {
        // The topmost section intersecting the upper part of the viewport wins.
        const visible = observed
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActiveId(visible[0].target.id);
      },
      // Bias the band toward the top so the highlight tracks what's being read.
      { rootMargin: '-10% 0px -70% 0px', threshold: 0 },
    );
    for (const entry of entries) {
      const element = scope.querySelector<HTMLElement>(`[id="${entry.id}"]`);
      if (element) observer.observe(element);
    }
    return () => observer.disconnect();
  }, [entries, scopeRef]);

  if (entries.length < 2) return null;

  const jumpTo = (id: string): void => {
    const element = scopeRef.current?.querySelector<HTMLElement>(`[id="${id}"]`);
    if (!element) return;
    element.scrollIntoView({
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      block: 'start',
    });
    setActiveId(id);
  };

  return (
    <nav
      aria-label="Quote contents"
      // Pinned a few pixels to the right of the centred max-w-3xl (24rem half-width)
      // document, so it tracks the content rather than the viewport edge. Underscores are
      // Tailwind's escape for the spaces calc() requires around its operators.
      className="fixed top-8 z-20 hidden max-h-[calc(100vh_-_4rem)] w-52 overflow-y-auto rounded-2xl border border-slate-200 bg-white p-4 shadow-sm xl:left-[calc(50%_+_24rem_+_0.5rem)] xl:block"
    >
      <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
        Contents
      </p>
      <ul className="space-y-0.5 border-l border-slate-200">
        {entries.map((entry) => {
          const active = entry.id === activeId;
          return (
            <li key={entry.id}>
              <button
                type="button"
                onClick={() => jumpTo(entry.id)}
                className={`-ml-px block w-full border-l-2 py-1 pl-3 text-left text-xs transition-colors ${
                  entry.emphasis ? 'font-bold' : ''
                } ${
                  active
                    ? 'border-brand-500 font-medium text-brand-700'
                    : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700'
                }`}
              >
                {entry.label}
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

/**
 * The customer-facing quote document: header, sections (server-sanitized HTML), the two
 * product-line tables at their placements with selectable optional/bundle lines, and
 * the typed-signature approval form. Used by the public /q/:token page and by the
 * internal "Preview delivery page" modals (previewMode disables approving).
 */export function QuoteDeliveryView({
  view,
  token,
  previewMode = false,
  readOnly = false,
}: {
  view: PublicQuoteView;
  /** Delivery token for approval submission — omit in internal previews. */
  token?: string;
  previewMode?: boolean;
  /** View-only mode ("SOW for delivery team"): no approval form, no option toggles. */
  readOnly?: boolean;
}): JSX.Element {
  // Scopes the table of contents' anchor lookups to this rendered document (the editor
  // pages behind a preview modal reuse the same anchor ids).
  const rootRef = useRef<HTMLDivElement | null>(null);
  // The customer's working selection (ids of the optional/bundle lines picked),
  // seeded with the lines the quote author pre-selected. Bundle headers are radio
  // options — only the first preselected bundle stays checked.
  const [selected, setSelected] = useState<Set<string>>(() => {
    const headers = bundleHeaders(view.lines);
    const preselectedHeaders = [...headers.values()].filter((h) => h.preselect);
    const extraHeaderIds = new Set(preselectedHeaders.slice(1).map((h) => h.id));
    return new Set(
      view.lines
        .filter((l) => isSelectable(l, headers) && l.preselect && !extraHeaderIds.has(l.id))
        .map((l) => l.id),
    );
  });
  const [justApproved, setJustApproved] = useState(false);

  const headers = useMemo(() => bundleHeaders(view.lines), [view.lines]);

  const approved = Boolean(view.approvedAt) || justApproved;
  // Expired quotes render read-only — the approval form is withheld (the server
  // enforces this on submit too).
  const expired = view.expired && !approved;

  // Approved quotes render the frozen server-side selection; before approval the
  // customer's working selection decides what's included. Right after approving in
  // this session, the working selection IS the agreed one.
  const included = (line: QuoteLine): boolean => {
    if (approved && !justApproved) return line.isSelected;
    // Member lines of a header-owned bundle are in iff their bundle is chosen.
    if (!line.isBundleHeader && line.bundleKey) {
      const header = headers.get(line.bundleKey);
      if (header) return selected.has(header.id);
    }
    if (!isSelectable(line, headers)) return true;
    return selected.has(line.id);
  };
  const mins = bundleMinimums(view.lines);
  const maxes = bundleMaximums(view.lines);

  const toggle = (line: QuoteLine): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      // Bundle headers are a radio group across the whole quote: picking one
      // deselects every other bundle.
      if (line.isBundleHeader && line.bundleKey) {
        const wasPicked = next.has(line.id);
        for (const header of headers.values()) next.delete(header.id);
        if (!wasPicked) next.add(line.id);
        return next;
      }
      if (next.has(line.id)) {
        next.delete(line.id);
        return next;
      }
      // Bundle maximums: at the cap, min 1 / max 1 behaves like a radio group (the
      // previous pick deselects automatically); larger caps just refuse the extra pick.
      if (line.bundleKey) {
        const max = maxes.get(line.bundleKey);
        if (max !== undefined) {
          const pickedIds = view.lines
            .filter((l) => l.bundleKey === line.bundleKey && next.has(l.id))
            .map((l) => l.id);
          if (pickedIds.length >= max) {
            if (max === 1) {
              for (const id of pickedIds) next.delete(id);
            } else {
              return prev;
            }
          }
        }
      }
      next.add(line.id);
      return next;
    });
  };
  // The two line groups, each with its own frequency label and document placement.
  const clamp = (afterSection: number): number =>
    afterSection <= 0 ? 0 : Math.min(afterSection, view.sections.length);
  const groups = [
    {
      key: 'g1',
      lines: view.lines.filter((l) => l.lineGroup !== 2),
      frequencyLabel:
        view.lines1SectionLabel ?? QUOTE_LINES_FREQUENCY_LABELS[view.lines1Frequency],
      at: clamp(view.linesAfterSection),
      pageBreakBefore: view.lines1PageBreakBefore,
      pageBreakAfter: view.lines1PageBreakAfter,
      qtyLabel: view.lines1QtyLabel ?? 'Qty',
      unitPriceLabel: view.lines1UnitPriceLabel ?? 'Unit price',
      amountLabel: view.lines1AmountLabel ?? 'Amount',
      totalLabel:
        view.lines1TotalLabel ?? `${QUOTE_LINES_FREQUENCY_LABELS[view.lines1Frequency]} total`,
      qtyHidden: view.lines1QtyHidden,
      unitPriceHidden: view.lines1UnitPriceHidden,
      amountHidden: view.lines1AmountHidden,
      totalHidden: view.lines1TotalHidden,
    },
    {
      key: 'g2',
      lines: view.lines.filter((l) => l.lineGroup === 2),
      frequencyLabel:
        view.lines2SectionLabel ?? QUOTE_LINES_FREQUENCY_LABELS[view.lines2Frequency],
      at: clamp(view.lines2AfterSection),
      pageBreakBefore: view.lines2PageBreakBefore,
      pageBreakAfter: view.lines2PageBreakAfter,
      qtyLabel: view.lines2QtyLabel ?? 'Qty',
      unitPriceLabel: view.lines2UnitPriceLabel ?? 'Unit price',
      amountLabel: view.lines2AmountLabel ?? 'Amount',
      totalLabel:
        view.lines2TotalLabel ?? `${QUOTE_LINES_FREQUENCY_LABELS[view.lines2Frequency]} total`,
      qtyHidden: view.lines2QtyHidden,
      unitPriceHidden: view.lines2UnitPriceHidden,
      amountHidden: view.lines2AmountHidden,
      totalHidden: view.lines2TotalHidden,
    },
  ].filter((g) => g.lines.length > 0);

  // The "Approve this quote" block is placed by `approvalPlacement`: it is emitted at
  // the first matching anchor while the document is assembled, and falls through to the
  // bottom when the placement is `end` or names an anchor that isn't present (a lines
  // section with no lines renders no table, so `afterLines1/2` can go unclaimed). Hidden
  // sections still hold their slot, so `afterSectionN` always resolves.
  const approvalNode =
    !approved && !expired && !readOnly ? (
      <ApprovalForm
        key="approval"
        token={token}
        previewMode={previewMode}
        lines={view.lines}
        selected={selected}
        onApproved={() => setJustApproved(true)}
      />
    ) : null;
  let approvalPlaced = false;
  // Which section the approval block ended up following, so the table of contents can
  // list it in document order (null = it falls to the end).
  let approvalAfterSection: number | null = null;
  const takeApproval = (
    anchor: QuoteApprovalPlacement,
    afterSectionIndex: number | null,
  ): JSX.Element[] => {
    if (!approvalNode || approvalPlaced || view.approvalPlacement !== anchor) return [];
    approvalPlaced = true;
    approvalAfterSection = afterSectionIndex;
    return [approvalNode];
  };

  const tablesAt = (position: number): JSX.Element[] =>
    groups
      .filter((g) => g.at === position)
      .flatMap((g) => [
        // The wrapper carries the TOC anchor; `scroll-mt-6` keeps the table clear of
        // the viewport edge when the table of contents scrolls to it.
        <div key={g.key} id={linesAnchorId(g.key)} className="scroll-mt-6">
          <LinesTable
            lines={g.lines}
            frequencyLabel={g.frequencyLabel}
            mins={mins}
            maxes={maxes}
            headers={headers}
            included={included}
            onToggle={approved || readOnly ? undefined : toggle}
            approved={approved}
            hidePricing={readOnly}
            qtyLabel={g.qtyLabel}
            unitPriceLabel={g.unitPriceLabel}
            amountLabel={g.amountLabel}
            totalLabel={g.totalLabel}
            qtyHidden={g.qtyHidden}
            unitPriceHidden={g.unitPriceHidden}
            amountHidden={g.amountHidden}
            totalHidden={g.totalHidden}
            pageBreakBefore={g.pageBreakBefore}
            pageBreakAfter={g.pageBreakAfter}
          />
        </div>,
        // The approval block can anchor to a line table, so claim it here too. A table at
        // `position` sits just after the section at `position - 1`.
        ...takeApproval(
          approvalAnchorForLineGroup(g.key === 'g1' ? 1 : 2),
          position > 0 ? position - 1 : null,
        ),
      ]);

  const blocks: JSX.Element[] = [];
  blocks.push(...tablesAt(0));
  view.sections.forEach((section, index) => {
    if (section.title?.trim() || section.html?.trim()) {
      blocks.push(
        // `scroll-mt-6` keeps the heading clear of the viewport edge when the table of
        // contents scrolls to it.
        <section
          key={`s-${index}`}
          id={sectionAnchorId(index)}
          className="mb-6 scroll-mt-6"
          style={pageBreakStyle(section.pageBreakBefore, section.pageBreakAfter)}
        >
          {section.title?.trim() ? (
            <h2 className="mb-2 border-b border-slate-200 pb-1 text-base font-semibold text-slate-800">
              {section.title}
            </h2>
          ) : null}
          {section.html ? (
            <div
              className="tiptap-content text-sm text-slate-700"
              // Sanitized server-side (scripts/event handlers/javascript: URLs stripped).
              dangerouslySetInnerHTML={{ __html: section.html }}
            />
          ) : null}
        </section>,
      );
    }
    // Line tables placed after this section render first, then the approval block, so
    // "after section N" lands below everything that section owns.
    blocks.push(...tablesAt(index + 1));
    const anchor = approvalAnchorForSection(index + 1);
    if (anchor) blocks.push(...takeApproval(anchor, index));
  });

  // Only labelled sections can be listed — an untitled section has nothing to link by.
  // The line-group tables join them at their placements, and the approval block at
  // whatever position it was emitted.
  const approvalEntryAfter = approvalPlaced ? approvalAfterSection : null;
  const showApprovalEntry = approvalNode !== null;
  const tocEntries = useMemo<TocEntry[]>(() => {
    const approvalEntry: TocEntry = {
      id: APPROVAL_ANCHOR_ID,
      label: 'Approve this quote',
      emphasis: true,
    };
    // Line tables at `position` render just before the section at that index, so their
    // TOC entries slot in the same way (position 0 = before every section).
    const linesEntriesAt = (position: number): TocEntry[] =>
      groups
        .filter((g) => g.at === position)
        .map((g) => ({ id: linesAnchorId(g.key), label: g.frequencyLabel }));
    const entries = [
      ...linesEntriesAt(0),
      ...view.sections.flatMap((section, index) => {
        const label = section.title?.trim();
        const own = label ? [{ id: sectionAnchorId(index), label }] : [];
        // Document order after a section: its line tables, then the approval block.
        const lines = linesEntriesAt(index + 1);
        const approval =
          showApprovalEntry && approvalEntryAfter === index ? [approvalEntry] : [];
        return [...own, ...lines, ...approval];
      }),
    ];
    // Not anchored to a section (placement `end`, or an anchor that never resolved).
    if (showApprovalEntry && !entries.some((e) => e.id === APPROVAL_ANCHOR_ID)) {
      entries.push(approvalEntry);
    }
    return entries;
    // `groups` derives solely from `view`, so it is intentionally represented by `view`.
  }, [view, showApprovalEntry, approvalEntryAfter]);

  return (
    <div ref={rootRef}>
      <QuoteTableOfContents entries={tocEntries} scopeRef={rootRef} />

      {approved ? (
        <div className="mb-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          {justApproved
            ? 'Thank you! Your approval has been recorded — a confirmation email is on its way.'
            : `This quote was approved${view.signerName ? ` by ${view.signerName}` : ''} and is shown as agreed.`}
        </div>
      ) : null}

      {expired ? (
        <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          This quote expired on {view.expirationDate} and can no longer be approved online.
          Please contact your Catalyst representative for an updated quote.
        </div>
      ) : null}

      <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
        {readOnly ? null : (
          <div className="mb-4 flex justify-end">
            <DownloadPdfButton token={token} previewMode={previewMode} />
          </div>
        )}
        <header className="mb-6 flex flex-wrap items-start justify-between gap-4 border-b-2 border-slate-800 pb-4">
          <div>
            <h1 className="text-xl font-semibold text-slate-900">{view.opportunityName}</h1>
            <p className="text-sm text-slate-500">Prepared for {view.customerName}</p>
          </div>
          <div className="text-right text-xs text-slate-500">
            {view.opportunityTypeName ? <p>{view.opportunityTypeName}</p> : null}
            <p>Quote version {view.version}</p>
            <p>Quote date: {view.date}</p>
            {view.expirationDate ? (
              <p className={expired ? 'font-medium text-amber-600' : undefined}>
                Expires: {view.expirationDate}
              </p>
            ) : null}
          </div>
        </header>
        {blocks}

        {approvalPlaced ? null : approvalNode}
      </div>
    </div>
  );
}
